import { useForm, useWatch } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useMemo, useState, useEffect, useRef } from "react";
import {
  CallApiGetVerifyCode,
  CallApiUpdateProfile,
} from "../Api/Enforcement";
import { SwalSuccess } from "../Share/Swal";
import { useNavigate } from "react-router-dom";
import { login, clientUpdateProfile } from "../Api/SubUrl";
import { ShowCustomErrorModal } from "../Components/Atoms/CustomErrorModal";
import useReCaptcha from "./useReCaptcha";
import { formatDateToIso, parseProfileDateValue } from "../Utils";
import { verifyClientSession } from "../Share/Func/clientSession";

const ERROR_MESSAGES = {
  NETWORK_ERROR: "Thực hiện chưa thành công!\n Liên hệ để được hỗ trợ",
  RECAPTCHA_INVALID: "Đã xảy ra lỗi : \nreCAPTCHA không hợp lệ!",
  GENERIC_ERROR: "Thử tải lại trang!\n Hoặc liên hệ fanpage để được hỗ trợ",
};

const VERIFY_CODE_COOLDOWN_MS = 3 * 60 * 1000;
const MINIMUM_AGE = 18;
const UNDER_AGE_MESSAGE = "Bạn chưa đủ 18 tuổi.";
const VIETNAM_PHONE_REGEX = /^(0|\+84)(3|5|7|8|9)[0-9]{8}$/;

const normalizePhone = (phone = "") => phone.trim();

const normalizePhoneForApi = (phone = "") => {
  const trimmed = normalizePhone(phone);
  if (trimmed.startsWith("+84")) {
    return `0${trimmed.slice(3)}`;
  }
  return trimmed;
};

const calculateAge = (dateOfBirth) => {
  const birthDate = parseProfileDateValue(dateOfBirth);
  if (!birthDate) {
    return null;
  }

  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const hasBirthdayPassed =
    today.getMonth() > birthDate.getMonth() ||
    (today.getMonth() === birthDate.getMonth() &&
      today.getDate() >= birthDate.getDate());

  if (!hasBirthdayPassed) {
    age -= 1;
  }

  return age;
};

export default function useUpdateProfile() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [isSentPhoneVerify, setIsSentPhoneVerify] = useState(false);
  const [isSendingPhoneVerify, setIsSendingPhoneVerify] = useState(false);
  const [loading, setLoading] = useState(false);
  const verifyCooldownTimerRef = useRef(null);

  const {
    version,
    reCaptchaV2,
    isReCaptchaReady,
    isLoadReCaptChaV3,
    recaptchaLoading,
    recaptchaError,
    getReCaptchaToken,
    resetReCaptcha,
    isDefaultV3,
  } = useReCaptcha("updateProfile", clientUpdateProfile);

  useEffect(() => {
    let cancelled = false;

    verifyClientSession().then((user) => {
      if (cancelled) return;
      setPageLoading(false);

      if (!user?.AccountName) {
        navigate(login, { replace: true });
        return;
      }

      setUser(user);
    });

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  useEffect(() => {
    return () => {
      if (verifyCooldownTimerRef.current) {
        clearTimeout(verifyCooldownTimerRef.current);
      }
    };
  }, []);

  const updateSchemaForm = yup.object().shape({
    userName: yup.string().required("Vui lòng nhập tên tài khoản"),
    fullName: yup
      .string()
      .trim()
      .required("Vui lòng nhập họ và tên.")
      .min(5, "Họ và tên phải có độ dài từ 5 đến 32 ký tự")
      .max(32, "Họ và tên phải có độ dài từ 5 đến 32 ký tự")
      .matches(
        /^[\p{L}\s]+$/u,
        "Họ và tên chỉ được sử dụng chữ cái và khoảng trắng"
      ),
    phone: yup
      .string()
      .trim()
      .required("Vui lòng nhập số điện thoại")
      .matches(VIETNAM_PHONE_REGEX, {
        message: "Số điện thoại không hợp lệ.",
        excludeEmptyString: true,
      }),
    dateOfBirth: yup
      .date()
      .nullable()
      .transform((_, originalValue) => {
        if (!originalValue) return null;
        return parseProfileDateValue(originalValue) ?? new Date(NaN);
      })
      .required("Vui lòng nhập ngày sinh")
      .typeError("Ngày sinh không hợp lệ")
      .test("is-old-enough", UNDER_AGE_MESSAGE, (value) => {
        if (!value) return false;
        const age = calculateAge(value);
        return age !== null && age >= MINIMUM_AGE;
      }),
    phoneOtp: yup.string().trim().required("Vui lòng nhập mã OTP"),
    password: yup.string().required("Vui lòng nhập mật khẩu"),
  });

  const { control, handleSubmit, reset } = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: useMemo(
      () => ({
        userName: "",
        fullName: "",
        phone: "",
        dateOfBirth: "",
        phoneOtp: "",
        password: "",
      }),
      []
    ),
    resolver: yupResolver(updateSchemaForm),
  });

  useEffect(() => {
    if (!user?.AccountName) return;

    reset({
      userName: user.AccountName,
      fullName: user?.FullName ?? "",
      phone: normalizePhone(user?.Phone ?? ""),
      dateOfBirth: formatDateToIso(user?.DateOfBirth),
      phoneOtp: "",
      password: "",
    });
  }, [user, reset]);

  const phone = useWatch({ control, name: "phone" });

  const startPhoneVerifyCooldown = useCallback(() => {
    setIsSentPhoneVerify(true);

    if (verifyCooldownTimerRef.current) {
      clearTimeout(verifyCooldownTimerRef.current);
    }

    verifyCooldownTimerRef.current = setTimeout(() => {
      setIsSentPhoneVerify(false);
      verifyCooldownTimerRef.current = null;
    }, VERIFY_CODE_COOLDOWN_MS);
  }, []);

  const handleGetPhoneVerifyCode = useCallback(async () => {
    if (isSendingPhoneVerify || isSentPhoneVerify || loading) return;

    if (!user?.AccountName) {
      ShowCustomErrorModal({
        message: "<p>Không thể tải thông tin tài khoản!</p>",
        title: "Đã xảy ra lỗi",
      });
      return;
    }

    const phoneNumber = normalizePhoneForApi(phone);
    if (!phoneNumber) {
      ShowCustomErrorModal({
        message: "<p>Vui lòng nhập số điện thoại để lấy OTP!</p>",
        title: "Đã xảy ra lỗi",
      });
      return;
    }

    if (!VIETNAM_PHONE_REGEX.test(phoneNumber)) {
      ShowCustomErrorModal({
        message: "<p>Số điện thoại không hợp lệ.</p>",
        title: "Đã xảy ra lỗi",
      });
      return;
    }

    setIsSendingPhoneVerify(true);

    try {
      const token = await getReCaptchaToken("getVerifyCode");
      const payload = {
        AccountName: user.AccountName,
        PhoneNumber: phoneNumber,
      };

      CallApiGetVerifyCode(token, payload, (res) => {
        setIsSendingPhoneVerify(false);
        resetReCaptcha();

        if (res?.Status) {
          const response = res.Status;
          const errorMessage =
            typeof response.data === "string"
              ? response.data
              : response.data?.Message ??
                "Thử tải lại trang!\n Hoặc liên hệ fanpage để được hỗ trợ";

          ShowCustomErrorModal({
            message: `<p>${errorMessage}</p>`,
            title: response.status === 400 ? "Cập nhật thất bại" : "Đã xảy ra lỗi",
          });
          return;
        }

        if (!res?.success) {
          ShowCustomErrorModal({
            message: "Thử tải lại trang!\n Hoặc liên hệ fanpage để được hỗ trợ",
            title: "Đã xảy ra lỗi",
          });
          return;
        }

        startPhoneVerifyCooldown();
      });
    } catch (error) {
      setIsSendingPhoneVerify(false);
      resetReCaptcha();
      ShowCustomErrorModal({
        message: error.message,
        title: "Xác thực thất bại",
      });
    }
  }, [
    getReCaptchaToken,
    isSendingPhoneVerify,
    isSentPhoneVerify,
    loading,
    phone,
    resetReCaptcha,
    startPhoneVerifyCooldown,
    user,
  ]);

  const handleUpdate = useCallback(
    async (formData) => {
      if (loading || pageLoading || isSendingPhoneVerify) return;

      try {
        const token = await getReCaptchaToken();
        setLoading(true);

        const payload = {
          AccountName: formData.userName,
          FullName: formData.fullName,
          DateOfBirth: formatDateToIso(formData.dateOfBirth),
          PhoneNumber: normalizePhoneForApi(formData.phone),
          OTP: formData.phoneOtp,
        };

        CallApiUpdateProfile(token, payload, (res) => {
          setLoading(false);
          if (res.Status) {
            resetReCaptcha();
            const status = res.Status.status;
            const message =
              typeof res.Status.data === "string"
                ? res.Status.data
                : res.Status.data?.Message;
            if (status === 400)
              ShowCustomErrorModal({
                message: `<p>${message || "Cập nhật thất bại"}</p>`,
                title: "Cập nhật thất bại",
              });
            else if (status === 403)
              ShowCustomErrorModal({
                message: ERROR_MESSAGES.RECAPTCHA_INVALID,
                title: "Xác thực thất bại",
              });
            else
              ShowCustomErrorModal({
                message: ERROR_MESSAGES.GENERIC_ERROR,
                title: "Đã xảy ra lỗi",
              });
          } else {
            if (res !== "success") {
              resetReCaptcha();
              ShowCustomErrorModal({
                message: ERROR_MESSAGES.NETWORK_ERROR,
                title: "Đã xảy ra lỗi",
              });
              return;
            }
            SwalSuccess("Cập nhật thành công!");
            navigate(login);
          }
        });
      } catch (error) {
        setLoading(false);
        resetReCaptcha();
        ShowCustomErrorModal({
          message: error.message || ERROR_MESSAGES.NETWORK_ERROR,
          title: "Cập nhật thất bại",
        });
      }
    },
    [getReCaptchaToken, isSendingPhoneVerify, loading, navigate, pageLoading, resetReCaptcha]
  );

  const handleInvalid = useCallback((errors) => {
    const errorMessages = Object.values(errors)
      .map((err) => `<p>${err.message}</p>`)
      .join("");
    ShowCustomErrorModal({
      message: errorMessages,
      title: "Cập nhật thất bại",
    });
  }, []);

  const isFormDisabled = pageLoading || loading || isSendingPhoneVerify;

  return {
    user,
    control,
    handleSubmit: handleSubmit(handleUpdate, handleInvalid),
    isSentPhoneVerify,
    isSendingPhoneVerify,
    handleGetPhoneVerifyCode,
    pageLoading,
    isFormDisabled,
    version,
    reCaptchaV2,
    isReCaptchaReady,
    isLoadReCaptChaV3,
    recaptchaLoading,
    recaptchaError,
    isDefaultV3,
    loading,
  };
}
